import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { areRatesLoading, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { min, EmptyArray } from 'lib/array/arrayUtils'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useCroRoomAmenitiesInsteadOfDescription from 'hooks/OfferPage/useCroRoomAmenitiesInsteadOfDescription'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'
import ModalContext from 'contexts/ModalContext'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import { toOfferPageSnapshotId } from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotText'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import WalledContent from 'components/Common/WalledContent'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons/BookmarkButton'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import FlashHotelRoomDetailsModal from '../FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import AccommodationSameRoomTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTile'
import AccommodationSameRoomTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileDetails'
import AccommodationSameRoomTileImage from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileImage'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import { getAccommodationRoomTileActionsState } from 'components/AccommodationKit/getAccommodationRoomTileActionsState'
import FlashHotelRateOption from './FlashHotelRateOption'
import useToggle from 'hooks/useToggle'
import { AccommodationSameRoomTileOptions } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileOptions'
import useChildrenStayFree from 'hooks/Offers/useChildrenStayFree'
import { getDynamicPricingUrgencyLabelData } from 'components/OfferPage/utils'
import scrollToVisibleOfferPageRoom from 'components/OfferPage/scrollToVisibleOfferPageRoom'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import FlashHotelSoldOutDatesCarousel from '../FlashHotelSoldOutDatesCarousel'
import { useAirportTransferPackageOptions, useAirportTransferPackageSelection } from './useAirportTransferPackageOptions'

interface Props {
  offer: App.HotelOffer
  packageViews: Array<App.OfferPackageOptionView>
  rooms: Array<OfferPageRoom>
  selectedPackageKey?: string
  checkIn?: string
  isLoading: boolean
  availableRoomCount?: number
  availableForPurchase?: boolean
  previewMode: boolean
  canRedeemLuxPlusBenefits: boolean
  showSelection?: boolean
  headerRef?: React.RefObject<HTMLDivElement | null>
  suggestedPackageOption?: {
    option: App.OfferPackageOptionView
    message: string
  }
  ratesLayout: 'stacked' | 'carousel'
  expandedPackageInclusions?: OfferPageState['expandedPackageInclusions']
}

// Exported so other consumers that need to mirror exactly which packages this component actually
// renders a price panel for (rather than every package the offer has) can reuse this single
// definition instead of risking their own copy drifting from it.
export function isRenderablePackageView(packageView: App.OfferPackageOptionView, requiresRates: boolean) {
  return packageView.capacityValid && packageView.hotelAvailable && (!requiresRates || !!packageView.hasAvailableRates)
}

function FlashHotelSameRoomOption(props: Props) {
  const {
    offer,
    packageViews,
    rooms,
    selectedPackageKey,
    checkIn,
    isLoading,
    availableRoomCount,
    availableForPurchase,
    canRedeemLuxPlusBenefits,
    showSelection = false,
    headerRef,
    suggestedPackageOption,
    ratesLayout,
    previewMode,
    expandedPackageInclusions,
  } = props

  const intl = useIntl()
  const isMultiRoom = rooms.length > 1
  const roomCount = rooms.length

  const firstPackageOption = packageViews[0]?.package
  const pkg = isHotelPackage(firstPackageOption) ? firstPackageOption : undefined
  const roomType = pkg?.roomType

  const cachedRoomDateData = useUrgentOfferRoomDateData(offer.id, roomType?.id)
  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0]?.availableRooms), [cachedRoomDateData, packageViews])

  const displayedRoomsBeforeSurcharge = useMemo(() => {
    if (!!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes') {
      return liveRoomsBeforeSurcharge
    }
    return undefined
  }, [liveRoomsBeforeSurcharge])

  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const contactContext = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const requiresRates = !!offer.property?.useDynamicCancellationPolicies && !!checkIn

  const carouselRef = useRef<HTMLDivElement | null>(null)

  const { value: localInclusionsOpen, toggle: onInclusionsToggle, on: expandInclusions } = useToggle()
  const expandRequestNonce = roomType && expandedPackageInclusions?.roomTypeId === roomType.id
    ? expandedPackageInclusions.nonce
    : undefined
  const inclusionsOpen = localInclusionsOpen || expandRequestNonce !== undefined

  useLayoutEffect(() => {
    if (expandRequestNonce === undefined || !roomType) return
    expandInclusions()
    offerPageDispatch({ type: OfferPageActions.clearExpandedPackageInclusions })
    scrollToVisibleOfferPageRoom(roomType.id)
  }, [expandInclusions, expandRequestNonce, offerPageDispatch, roomType])

  const {
    getPackageSelection,
    isRenderedPackageViewSelected,
    onAirportTransferToggle,
  } = useAirportTransferPackageSelection(rooms, selectedPackageKey)

  // Availability calculations
  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable) || previewMode,
  ], [packageViews, previewMode])

  const minAvailableRooms = min(packageViews, option => (option.availableRooms ?? 0))?.availableRooms
  const hasExceededCapacity = packageViews.every(option => option.hotelAvailable && !option.capacityValid)
  const renderablePackageViews = useMemo(
    () => packageViews.filter(option => isRenderablePackageView(option, requiresRates)),
    [packageViews, requiresRates],
  )
  const capacityValidPackageViews = useMemo(
    () => packageViews.filter(option => option.capacityValid),
    [packageViews],
  )
  const availablePackages = useMemo(
    () => renderablePackageViews.filter(option => (option.availableRooms ?? 0) >= roomCount),
    [renderablePackageViews, roomCount],
  )
  const isAllRoomsAvailable = packageViews.every(option => option.availableRooms && option.availableRooms >= roomCount)
  const isAllRoomsHaveAvailabilityData = packageViews.every(option => typeof option.availableRooms === 'number')
  // Use explicit === 0 check to distinguish "sold out" (0) from "loading" (undefined)
  const isSoldOut = !!checkIn && packageViews.every(option => option.availableRooms === 0)
  const { isAnyVariant: showSoldOutDatesCarousel } = useExperiment(Experiments.croSoldOutFlashPackagesState, {
    canActivate: isSoldOut,
  })
  const hasHotelMemberPrice = packageViews.some(option => option.hotelMemberPrice > 0)

  // For multi-room: check if selection is enabled
  const isSelectionEnabled = isMultiRoom
    ? (isAllRoomsAvailable && !isSoldOut && !hasExceededCapacity)
    : (datesAvailable && roomFitsCapacity)

  const callbacksFeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)
  const shouldPreventMultipleRoomsBooking = roomCount > 2 && canRedeemLuxPlusBenefits && hasHotelMemberPrice

  const canActivatePopularOptionBookingRemaining = !!availableRoomCount && !!checkIn
  const { isAnyVariant: isPopularOptionBookingRemainingVariant } = useExperiment(
    Experiments.croPopularOptionBookingRemaining,
    { canActivate: canActivatePopularOptionBookingRemaining },
  )
  const croPopularOptionBookingRemaining = canActivatePopularOptionBookingRemaining && isPopularOptionBookingRemainingVariant

  const includedGuestsByRate = useMemo(() => packageViews.map(view => view.package.roomRate?.includedGuests ?? []), [packageViews])

  const { freeStayLabel } = useChildrenStayFree({
    offerType: offer.type,
    filters: { rooms },
    includedGuestsByRate,
  })

  const cheapestPackageView = useMemo(
    () => min(packageViews, view => view.hotelPrice),
    [packageViews],
  )

  // Compute the state for AccommodationRoomTileActions
  // Don't show error messages when phone booking is required
  const actionsState = useMemo(() => {
    if (shouldPreventMultipleRoomsBooking) {
      return undefined
    }
    return getAccommodationRoomTileActionsState({
      availableForPurchase,
      roomFitsCapacity,
      datesAvailable,
      isMultiRoom,
      checkIn,
      isSoldOut,
      hasExceededCapacity,
      isAllRoomsAvailable,
      isAllRoomsHaveAvailabilityData,
    })
  }, [
    availableForPurchase,
    checkIn,
    datesAvailable,
    hasExceededCapacity,
    isAllRoomsAvailable,
    isAllRoomsHaveAvailabilityData,
    isMultiRoom,
    isSoldOut,
    roomFitsCapacity,
    shouldPreventMultipleRoomsBooking,
  ])

  // Event handlers
  const handleOpenCalendar = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, roomType])

  const onSelectIndividual = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
    headerRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
  }, [offerPageDispatch, headerRef])

  const onShowRoomDetails = useCallback(() => {
    if (roomType) {
      showModal(<FlashHotelRoomDetailsModal room={roomType} />)
    }
  }, [roomType, showModal])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])

  const basePackagesToRender = useMemo<Array<App.OfferPackageOptionView>>(() => {
    if (previewMode) {
      return capacityValidPackageViews
    }

    if (isMultiRoom) {
      return availablePackages
    }

    return renderablePackageViews
  }, [availablePackages, capacityValidPackageViews, isMultiRoom, previewMode, renderablePackageViews])
  const packagesToRender = useAirportTransferPackageOptions(basePackagesToRender)
  const carouselWidth = packagesToRender.length > 1 ? rem(300) : '100%'
  const [roomDetailsInViewRef, roomDetailsInView] = useInView({ triggerOnce: true })
  const hasHotelRoomAmenities = packagesToRender.some(view => (
    isHotelPackage(view.package) && view.package.roomType.amenities.length > 0
  ))
  const { isAnyVariant: shouldShowRoomAmenitiesInsteadOfDescription } = useCroRoomAmenitiesInsteadOfDescription({
    canActivate: hasHotelRoomAmenities && roomDetailsInView,
  })
  const showRoomAmenitiesInsteadOfDescription = hasHotelRoomAmenities && shouldShowRoomAmenitiesInsteadOfDescription

  // Selection tracking for multi-room
  const selectedPackageOptionViewIndex = useMemo(() => {
    if (!isMultiRoom) return 0
    return packagesToRender.findIndex(isRenderedPackageViewSelected) + 1
  }, [isMultiRoom, isRenderedPackageViewSelected, packagesToRender])

  // Auto-scroll to selection in multi-room mode
  useEffect(() => {
    if (!carouselRef.current || !isMultiRoom) return
    if (selectedPackageOptionViewIndex && showSelection) {
      const selectedElement = carouselRef.current.children[selectedPackageOptionViewIndex - 1]
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' })
      }
    }
  }, [selectedPackageOptionViewIndex, showSelection, isMultiRoom])

  if (!pkg || !roomType || !cheapestPackageView) {
    return null
  }

  const availabilityText = NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)
    ? intl.formatMessage({
        id: 'xhccxef8IIv04RSBD16X',
        defaultMessage: '7 days a week from 9am to 7pm',
        description: 'Availability text for regions without 24/7 support',
      })
    : intl.formatMessage({
        id: 'URuGV_rVgQS0QR4q8ItC',
        defaultMessage: '24 hours, 7 days',
        description: 'Availability text for 24/7 support in the customer support section',
      })

  // Phone booking banner for special case
  const phoneBookingBanner = shouldPreventMultipleRoomsBooking ? (
    <Sheet background="light-background" padding={20}>
      <Group direction="vertical" gap={8}>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '2pulspoB9cHo6Ojkjeaw',
              defaultMessage: 'Ready to book? Call us to complete your booking',
              description: 'Heading for phone booking banner in the booking or order summary view',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: 'UmTsldbaPmHF0U43v43X',
                defaultMessage: 'Our team are available {availability}',
                description: 'Informational text about customer support team availability and operating hours',
              },
              {
                availability: availabilityText,
              },
            )}
          </BodyText>
        </Group>
        <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${contactContext.defaultContact.number}`} fit="flex">
          {intl.formatMessage(
            {
              id: 'Npqv8j2BH95_AcfFxFlX',
              defaultMessage: 'Call {phoneNumber}',
              description: 'Action button label to call phone number on the offer detail page',
            },
            {
              phoneNumber: contactContext.defaultContact.humanReadable,
            },
          )}
        </TextButton>
        {callbacksFeatureEnabled && (
          <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
            {intl.formatMessage({
              id: 'w-5Xdyx85GaBmirZUAgh',
              defaultMessage: 'Request a callback',
              description: 'Action button label to request a callback on the offer detail page',
            })}
          </TextButton>
        )}
      </Group>
    </Sheet>
  ) : null

  const packageOptions = packagesToRender.map((view, index) => {
    const airportTransferPackageOption = view.airportTransferPackageOption
    const {
      airportTransferBasePackageKey,
      airportTransferSelected,
      selectedPackageView,
    } = getPackageSelection(view)

    const snapshotPackageId = toOfferPageSnapshotId(view.package.uniqueKey)

    return (
      <FlashHotelRateOption
        key={view.package.uniqueKey}
        focusTargetId={snapshotPackageId ? `package:${snapshotPackageId}` : undefined}
        packageView={selectedPackageView}
        displayPackageView={airportTransferPackageOption?.basePackageView}
        roomsBeforeSurcharge={displayedRoomsBeforeSurcharge}
        packagePosition={packagesToRender.length > 1 ? index + 1 : undefined}
        inclusionsOpen={inclusionsOpen}
        onInclusionsToggle={onInclusionsToggle}
        suggestedPackageMessage={suggestedPackageOption?.option.package.uniqueKey === selectedPackageView.package.uniqueKey ? suggestedPackageOption.message : undefined}
        airportTransferPriceDifference={airportTransferPackageOption?.priceDifference}
        airportTransferInclusion={airportTransferPackageOption?.transferInclusion}
        airportTransferBasePackageKey={airportTransferBasePackageKey}
        airportTransferSelected={airportTransferSelected}
        onAirportTransferToggle={onAirportTransferToggle}
      />
    )
  })

  return (
    <Pane data-testid="multi-same-room-room">
      <ScrollAnchor id={roomType.id} vOffset={rem(80)} />

      {/* Selection indicator for multi-room */}
      {isMultiRoom && !!selectedPackageOptionViewIndex && showSelection && (
        <Sheet background="highlight-primary" padding="4 20">
          <CaptionTextBlock variant="medium" startIcon={<LineArrowDownIcon />}>
            {intl.formatMessage({
              id: '9RnDXmXoFPP3Tj-YnCU9',
              defaultMessage: 'Your current selection',
              description: 'Label indicating the current room selection on the room page',
            })}
          </CaptionTextBlock>
        </Sheet>
      )}

      <AccommodationSameRoomTile roomPackage={pkg}>
        <AccommodationSameRoomTileImage roomPackage={pkg} />
        <AccommodationSameRoomTileDetails
          roomPackage={pkg}
          roomCount={roomCount}
          onShowRoomDetails={onShowRoomDetails}
          showHotelRoomAmenities={showRoomAmenitiesInsteadOfDescription}
          containerRef={roomDetailsInViewRef}
          urgencyLabels={<>
            {freeStayLabel && <PromotionLabel icon={<LineKidIcon />}>{freeStayLabel}</PromotionLabel>}
            {!datesAvailable && <UrgencyLabel variant="critical2">
              {intl.formatMessage({
                id: 'r939r00GL7ijU93v_IDp',
                defaultMessage: 'SOLD OUT',
                description: 'Label indicating sold out status on the offer detail page',
              })}
            </UrgencyLabel>}
            {checkIn && !croPopularOptionBookingRemaining && !displayedRoomsBeforeSurcharge && (
              <RoomsRemainingLabel roomCount={isMultiRoom ? (minAvailableRooms ?? 0) : (cheapestPackageView.availableRooms ?? 0)} threshold="rate" />
            )}
            {checkIn && croPopularOptionBookingRemaining && !displayedRoomsBeforeSurcharge && availableRoomCount && (
              <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} />
            )}
            {!!roomType.stats.booked && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && (
            <CSSBreakpoint min="desktop">
              <WishlistGuard>
                <BookmarkButton offer={offer} roomTypeId={roomType.id} />
              </WishlistGuard>
            </CSSBreakpoint>
          )}
        >
          <AccommodationRoomTileActions
            roomPackage={pkg}
            state={actionsState}
            shouldShowAmenitiesAndPoliciesCta={!showRoomAmenitiesInsteadOfDescription}
            onChangeOccupancyClick={openCapacityFilter}
            onChangeDatesClick={handleOpenCalendar}
            onMultiRoomModeClick={onSelectIndividual}
          />
          {phoneBookingBanner}

        </AccommodationSameRoomTileDetails>
        <AccommodationSameRoomTileOptions>
          {!isLoading && isSoldOut && showSoldOutDatesCarousel && <FlashHotelSoldOutDatesCarousel roomType={roomType} />}
          {/* Package carousel */}
          {!isLoading && isSelectionEnabled && !shouldPreventMultipleRoomsBooking && packagesToRender.length > 0 && (
            <WalledContent
              offerType={offer.type}
              enforced={offer.walledGarden}
              wall={(
                <Group fullHeight direction="vertical" verticalAlign="end">
                  <TextButton size="large" kind="secondary" onClick={onSignUpClick}>
                    {intl.formatMessage({
                      id: 'ifaKBmYHhtLQZ35Y4akM',
                      defaultMessage: 'Sign up to view exclusive rates',
                      description: 'Sign up button text to view exclusive rates on the offer detail page',
                    })}
                  </TextButton>
                </Group>
              )}
            >
              {ratesLayout === 'stacked' && packageOptions}
              {ratesLayout === 'carousel' && <CardCarousel
                snap="start"
                width={carouselWidth}
                gap={12}
                gutterSize={20}
                ref={carouselRef}
              >
                {packageOptions}
              </CardCarousel>}
            </WalledContent>
          )}
          {isLoading && <LoadingIndicator inline />}
        </AccommodationSameRoomTileOptions>
      </AccommodationSameRoomTile>
    </Pane>
  )
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'selectedPackageKey'
  | 'checkIn'
  | 'isLoading'
  | 'previewMode'
  | 'availableForPurchase'
  | 'canRedeemLuxPlusBenefits'
  | 'expandedPackageInclusions'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms ?? EmptyArray,
  selectedPackageKey: state.selectedPackageKey,
  checkIn: state.checkInDate,
  // When we must fetch availability from calendar service (i.e. `useDynamicCancellationPolicies` is
  // set), there may not be any package options to display, so we cannot reliably indicate loading
  // at the rate level, and must do it at the room level.
  isLoading: areRatesLoading(appState, state),
  previewMode: appState.system.previewMode,
  availableForPurchase: isAvailableForPurchase(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  expandedPackageInclusions: state.expandedPackageInclusions,
}), FlashHotelSameRoomOption)
